import React, {useCallback, useMemo} from 'react';
import {Stack, TooltipHost, useTheme, Text} from '@fluentui/react';
import {getListClassNames} from 'styles';
import {KeywordStatus, VexWorkbenchItem} from 'types';
import {useUserContext} from 'authentication/UserContext';
import {useTranslation} from 'react-i18next';
import {DateTimeFormat, formatDateTime} from 'i18n/localeDateFormat';
import i18n from 'i18n/i18n';
import {ArrayValues, StatusField} from 'components/EntityList/ColumnRenderers';
import {v4 as uuidv4} from 'uuid';

interface VexWorkbenchItemCellProps {
	keywordStatus: KeywordStatus;
	proportionTable: number[];
	halfHeight: boolean;
	item?: VexWorkbenchItem;
	columnKey?: string;
}

export const VexWorkbenchItemCell: React.FC<VexWorkbenchItemCellProps> = ({
	keywordStatus,
	proportionTable,
	halfHeight,
	item,
	columnKey,
}) => {
	const {myVexClusters} = useUserContext();
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VexWorkbenchItems',
	});
	const getItemDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			if (keywordStatus === KeywordStatus.Accepted) {
				return item.regulatoryDocument.workflow.dueDateInProcess;
			}
			if (keywordStatus === KeywordStatus.New) {
				return item.regulatoryDocument.workflow.dueDate;
			}
			return undefined;
		},
		[keywordStatus],
	);
	const isOverDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			const dueDate = getItemDueDate(item);
			return new Date() > new Date(dueDate);
		},
		[getItemDueDate],
	);

	const getDueDate = useCallback(
		(item: VexWorkbenchItem) => {
			const dueDate = getItemDueDate(item);
			return dueDate
				? formatDateTime(
						new Date(dueDate),
						i18n,
						DateTimeFormat.DateMonthWeekday,
				  )
				: '';
		},
		[getItemDueDate],
	);

	const itemClasses = useMemo(
		() =>
			item && isOverDueDate(item)
				? `${classNames.itemCellPadding0unfocused} ${classNames.itemCellError}`
				: classNames.itemCellPadding0unfocused,
		[classNames, isOverDueDate, item],
	);

	const commonFieldStyle = useMemo(() => {
		return {
			textAlign: 'center' as const,
		};
	}, []);

	const getSimpleField = useCallback(
		(content: any, index: number) => {
			return (
				<div
					style={{
						...commonFieldStyle,
						width: `${proportionTable[index]}%`,
					}}
				>
					{content}
				</div>
			);
		},
		[proportionTable, commonFieldStyle],
	);

	const shrinkNames = (names: string[]) => {
		return names.map(name => {
			if (name.length > 16) {
				return (
					<TooltipHost content={name} key={uuidv4()}>
						<div>{`${name.substring(0, 13)}...`}</div>
					</TooltipHost>
				);
			}
			return <div key={uuidv4()}>{`${name}`}</div>;
		});
	};

	const markets = useMemo(
		() => item?.regulatoryDocument?.regulation?.markets?.map(m => m.name) || [],
		[item],
	);

	const vexClusters = useMemo(
		() =>
			item?.regulatoryDocument?.paragraphs?.flatMap(par =>
				par.keywordAssignments?.flatMap(ka =>
					ka.vexClusterAssignments?.flatMap(vca => vca?.vexCluster),
				),
			) || [],
		[item],
	);

	const myVexClusterIdSet = useMemo(
		() => new Set(myVexClusters.map(vc => vc.id)),
		[myVexClusters],
	);

	const vexClusterNames = useMemo(() => {
		const uniqueNames = new Set<string>();

		vexClusters.forEach(vc => {
			if (vc && myVexClusterIdSet.has(vc.id) && vc.name) {
				uniqueNames.add(vc.name);
			}
		});

		return Array.from(uniqueNames);
	}, [vexClusters, myVexClusterIdSet]);

	const statusField = useMemo(() => {
		if (!item) return <></>;
		const status = item.regulatoryDocument?.status || '';
		return item.regulatoryDocument?.status ? (
			<StatusField
				item={item}
				enumName='RegulatoryDocumentStatus'
				value={status}
			/>
		) : (
			<></>
		);
	}, [item]);

	const paragraphCountSortedByVexClusters = useMemo(() => {
		if (!item) return [];

		const counts: Record<string, number> = {};

		item.regulatoryDocument?.paragraphs?.forEach(par => {
			const seenInParagraph = new Set<string>();

			par.keywordAssignments?.forEach(ka => {
				ka.vexClusterAssignments?.forEach(vca => {
					if (
						vca.keywordStatus === keywordStatus &&
						!seenInParagraph.has(vca.vexCluster.id)
					) {
						const name = vca.vexCluster?.name;
						if (name) {
							counts[name] = (counts[name] || 0) + 1;
						}
						seenInParagraph.add(vca.vexCluster.id);
					}
				});
			});
		});

		return vexClusterNames.map(vcName => `${counts[vcName] || '0'}`);
	}, [vexClusterNames, item, keywordStatus]);

	if (!item) {
		return <></>;
	}

	return (
		<div
			key={item.regulatoryDocument.id}
			className={itemClasses}
			data-is-focusable={true}
			style={{textDecoration: 'none'}}
		>
			<Stack
				horizontal
				horizontalAlign={'space-between'}
				styles={{root: {width: '100%', padding: '8px 0'}}}
			>
				<div style={{width: `${proportionTable[0]}%`, padding: '0 8px'}}>
					<a
						href={`/regulations/${item?.regulatoryDocument?.regulation?.id}/${
							item?.regulatoryDocument?.id
						}/paragraphs?filter=${
							keywordStatus === KeywordStatus.Accepted ? 'inProcess' : 'new'
						}`}
					>
						<Text>
							{item.regulatoryDocument.regulation?.regulationNumber ?? ''}
						</Text>
						<div style={{color: theme.palette.neutralDark}}>
							{item?.regulatoryDocument?.name} - {statusField}
						</div>
						{columnKey !== t('CompletedTitle') && (
							<div className={classNames.itemSubtext}>
								{`${t('DueDate')}:${getDueDate(item)}`}
							</div>
						)}
					</a>
				</div>
				{getSimpleField(
					<ArrayValues array={shrinkNames(markets)} limit={4} />,
					1,
				)}
				{getSimpleField(
					<ArrayValues array={shrinkNames(vexClusterNames)} limit={4} />,
					2,
				)}
				{columnKey !== t('CompletedTitle') && (
					<div style={{textAlign: 'right', width: `${proportionTable[3]}%`}}>
						<ArrayValues
							array={paragraphCountSortedByVexClusters}
							limit={4}
							forceColumnFlow={true}
						/>
					</div>
				)}
			</Stack>
		</div>
	);
};
